import {
  DialogPanel,
  DialogTitle,
  Dialog as HeadlessUiDialog,
  Transition,
  TransitionChild,
} from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/solid'

interface Props {
  children: React.ReactNode
  dismissible?: boolean
  onClose: () => void
  open: boolean
}

export default function Dialog({
  children,
  dismissible = true,
  open,
  onClose,
}: Props) {
  return (
    <Transition show={open}>
      {/*
        `z-50` is load-bearing. The backdrop and panel below are only `fixed`,
        so without a stacking context here they sit at `z-index: auto` and lose
        to any positioned page chrome with a z-index — `ContinueButton` is
        `sticky z-20`, and the funnel headers and footers are z-indexed too. The
        symptom is a dialog that dims the page but leaves the header and the
        sticky CTA bright and clickable on top of its own backdrop.
      */}
      <HeadlessUiDialog
        className="relative z-50"
        onClose={dismissible ? onClose : () => {}}
        open={open}
      >
        {/* NOTE: The backdrop, rendered as a fixed sibling to the panel container */}
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div aria-hidden="true" className="fixed inset-0 bg-black/70" />
        </TransitionChild>

        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          {/* NOTE: Scrollable dialog, see: https://headlessui.com/react/dialog#scrollable-dialogs */}
          <div className="fixed inset-0 w-screen overflow-y-auto p-4">
            <div className="flex min-h-full items-center justify-center">
              {/* NOTE: The actual dialog panel  */}
              <DialogPanel className="w-5/6 space-y-4 rounded-xl bg-white p-8 shadow-sm md:max-w-lg">
                {dismissible && (
                  <DialogTitle as="div" className="text-left">
                    <button
                      className="rounded-full hover:opacity-80"
                      onClick={onClose}
                      type="button"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </DialogTitle>
                )}

                {children}
              </DialogPanel>
            </div>
          </div>
        </TransitionChild>
      </HeadlessUiDialog>
    </Transition>
  )
}
